import { call, put, takeLatest } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import toast from 'react-hot-toast';
import { t } from 'i18next';
import client from '../../service/graphql';

import {
  GET_CLIENT_FOR_COACH,
  GET_CLIENT_BY_ID,
  CREATE_CLIENT_MUTATION,
  ARCHIVE_CLIENT_MUTATION,
  GET_ACTIVITIES_COMPLETED_BY_CLIENT,
  GET_CLIENT_OBJECTIVES_PROGRESS_DATA,
  GET_USER_ACTIVITY_PROGRESS,
  CLIENT_ACTIVITY_SUMMARY_GENERATION,
  GET_CLIENT_ACTIVITY_PROGRESS_SUMMARY,
  UPDATE_CLIENT_STATUS_MUTATION,
  GET_CLIENT_CONSENT,
  CREATE_CLIENT_CONSENT,
  UPDATE_CLIENT_CONSENT,
  RESEND_CLIENT_INVITATION,
  CREATE_CLIENT_GROUP_MUTATION,
} from '../../service/graphql/clientsQuery';
import {
  fetchClientsSuccess,
  fetchClientsFailure,
  fetchClientsRequest,
  fetchSelectedClientSuccess,
  fetchSelectedClientFailure,
  fetchSelectedClientRequest,
  IClient,
  IFetchClientPayload,
  IGetClientByIdPayload,
  addClientRequest,
  addClientSuccess,
  ICreateClientInput,
  getCompletedActivitiesRequest,
  getCompletedActivitiesSuccess,
  getCompletedActivitiesFailure,
  setFetchedActivityPaginationData,
  getEarlierActivitiesRequest,
  getEarlierActivitiesSuccess,
  getEarlierActivitiesFailure,
  getSelectedClientObjectivesDataRequest,
  IFetchObjectivesDataPayload,
  IGetCompletedActivityPayload,
  IClientProgressData,
  getSelectedClientObjectivesDataSuccess,
  getSelectedClientObjectivesDataFailure,
  getUserActivityProgressRequest,
  getUserActivityProgressSuccess,
  getUserActivityProgressFailure,
  IGetUserActivityProgressPayload,
  IUserActivityProgress,
  ICompletedActivity,
  generateClientActivityStatusSummeryRequest,
  generateClientActivityStatusSummerySuccess,
  generateClientActivityStatusSummeryFailure,
  getClientActivityProgressSummaryRequest,
  getClientActivityProgressSummarySuccess,
  getClientActivityProgressSummaryFailure,
  IActivitySummary,
  changeClientStatus,
  IChangeClientStatusPayload,
  changeClientStatusSuccess,
  changeClientStatusFailure,
  fetchArchivedClients,
  fetchArchivedClientsSuccess,
  fetchArchivedClientsFailure,
  archiveClientRequest,
  IArchiveClientPayload,
  archiveClientSuccess,
  archiveClientFailure,
  addClientFailure,
  fetchClientPrivacySettings,
  fetchClientPrivacySettingsFailure,
  fetchClientPrivacySettingsSuccess,
  createClientPrivacySettings,
  updateClientPrivacySettings,
  ICreatePrivacySettingsPayload,
  IUpdatePrivacySettingsPayload,
  IPrivacySettings,
  createClientPrivacySettingsSuccess,
  updateClientPrivacySettingsSuccess,
  updateClientPrivacySettingsFailure,
  setOpenPrivacySettingsConcentModal,
  resendClientInvitation,
  ICreateClientGroupInput,
  addClientGroupRequest,
} from './clientSlice';
import { IGraphQLResponse } from '../../types/commonTypes';
import {
  categorizeActivities,
  toastStyles,
  transformClientServerData,
  // transformClientServerData
} from '../../utils/utils';
import i18n from '../../i18n';
import { getGraphQLError, GraphQLErrorTypes } from '../../utils/errorMap';
import {
  ClientLimitStatus,
  ISubscriptionPlanStatus,
  PaymentStatus,
  updateSubscriptionPlanStatus,
} from '../coach/coachSlice';
import { ClientType } from '../../types/client';
// import Toast from '../../components/Toast/Toast';

export enum EngagementLevel {
  FULL,
  PARTIAL,
  NONE,
}
interface IEngagementLevel {
  clientId: string;
  engagementLevels: {
    date: string;
    engagementLevel: EngagementLevel;
  }[];
}

interface IClientsWithEngagementLevel {
  client: IClient;
  engagementLevel: IEngagementLevel;
}

export interface IClientWithTotalClientCount {
  totalClientCount: number;
  clients: IClientsWithEngagementLevel[];
  paymentStatus: PaymentStatus;
  clientCountStatus: ClientLimitStatus;
}

export interface IGetAllClientsResponse {
  getClientsByCoachId: {
    totalClientCount: number;
    clients: IClientsWithEngagementLevel[];
    subscriptionPlanStatus?: ISubscriptionPlanStatus;
    paymentStatus: PaymentStatus;
    clientCountStatus: ClientLimitStatus;
  };
}

interface ICreateClientResponse {
  createClient: IClient;
}

interface IGetClientByIdResponse {
  getClientById: IClient;
}

interface IArchieveClientResponse {
  deleteClient: IClient;
}
interface IGetObjectiveProgressDataResponse {
  getObjectiveProgress: IClientProgressData[];
}
interface IGetUserActivityProgressResponse {
  progressSummary: IUserActivityProgress[];
}

interface IGetClientActivityProgressSummaryResponse {
  clientActivityProgressSummary: {
    count: number;
    activitySummary: IActivitySummary[];
  };
}

interface IGetCompletedActivityResponse {
  getClientCompletedActivities: {
    completedActivities: ICompletedActivity[];
  };
}

export interface IChangeClientStatusResponse {
  changeClientStatus: {
    id: string;
    isActive: boolean;
  };
}

export interface IGetPrivacySettingsResponse {
  getClientConcent: IPrivacySettings;
}

export interface ICreateClientConcentResponse {
  createClientConcent: IPrivacySettings;
}

export interface IUpdateClientConcentResponse {
  updateClientConcent: IPrivacySettings;
}

function* watchFetchClients({ payload }: PayloadAction<IFetchClientPayload>) {
  const { coachId, nameText, skip, take, isArchived, orderBy, order, clientType, clientGroupId } =
    payload;
  try {
    const result: IGraphQLResponse<IGetAllClientsResponse> = yield call(client.query.bind(client), {
      query: GET_CLIENT_FOR_COACH,
      variables: {
        coachId,
        take,
        skip,
        nameText,
        isArchived,
        orderBy,
        order,
        clientType,
        clientGroupId,
      },
    });
    const transformedClients = transformClientServerData(
      result.data.getClientsByCoachId
    ) as IClientWithTotalClientCount;
    if (clientType !== ClientType.GROUP) {
      const { subscriptionPlanStatus, clientCountStatus } = result.data.getClientsByCoachId;

      yield put(updateSubscriptionPlanStatus({ subscriptionPlanStatus, clientCountStatus }));
    }

    if (isArchived) {
      yield put(fetchArchivedClientsSuccess(transformedClients));
    } else {
      yield put(fetchClientsSuccess(transformedClients));
    }
  } catch (error: unknown) {
    if (error instanceof Error) {
      if (isArchived) {
        yield put(fetchArchivedClientsFailure(error.message));
      } else {
        yield put(fetchClientsFailure(error.message));
      }
    }
  }
}

function* watchFetchSelectedClient({ payload }: PayloadAction<IGetClientByIdPayload>) {
  const { selectedClientId } = payload;
  try {
    const result: IGraphQLResponse<IGetClientByIdResponse> = yield call(client.query.bind(client), {
      query: GET_CLIENT_BY_ID,
      variables: { id: selectedClientId },
    });
    yield put(fetchSelectedClientSuccess(result.data.getClientById));
  } catch (error: unknown) {
    if (error instanceof Error) yield put(fetchSelectedClientFailure(error.message));
  }
}

function* watchAddClientRequest({ payload }: PayloadAction<ICreateClientInput>) {
  try {
    const { CreateClient } = payload;
    const result: IGraphQLResponse<ICreateClientResponse> = yield call(client.mutate.bind(client), {
      mutation: CREATE_CLIENT_MUTATION,
      variables: { CreateClient },
    });
    toast.success(i18n.t('addNewClient.toastMessage.clientCreated'), toastStyles.success);
    yield put(addClientSuccess(result.data.createClient));
    yield put(
      fetchClientsRequest({
        coachId: CreateClient.coachId,
        clientGroupId: CreateClient.clientGroupId,
        take: 10,
        skip: 0,
        isArchived: false,
        nameText: '',
      })
    );
  } catch (error: unknown) {
    if (error instanceof Error) {
      yield put(addClientFailure());
      if (error.message === 'Forbidden resource') {
        toast.error(i18n.t('forbiddenResourceErrorMessage'), toastStyles.error);
      } else {
        toast.error(error.message, toastStyles.error);
      }
    }
  }
}

function* watchAddClientGroupRequest({ payload }: PayloadAction<ICreateClientGroupInput>) {
  try {
    const result = yield call(client.mutate.bind(client), {
      mutation: CREATE_CLIENT_GROUP_MUTATION,
      variables: { createClientGroupInput: { ...payload } },
    });
    if (result.data.createClientGroup.success) {
      toast.success(result.data.createClientGroup.message, toastStyles.success);
      yield put(addClientSuccess(result.data.createClientGroupInput));
      yield put(
        fetchClientsRequest({
          coachId: payload.principalCoach,
          take: 10,
          skip: 0,
          isArchived: false,
          nameText: '',
          clientType: payload.clientType,
          ...(payload.clientGroupId && { clientGroupId: payload.clientGroupId }),
        })
      );
    } else {
      yield put(addClientFailure());
    }
  } catch (error: unknown) {
    if (error instanceof Error) {
      yield put(addClientFailure());
      if (error.message === 'Forbidden resource') {
        toast.error(i18n.t('forbiddenResourceErrorMessage'), toastStyles.error);
      } else {
        toast.error(error.message, toastStyles.error);
      }
    }
  }
}

function* watchArchieveClientRequest({ payload }: PayloadAction<IArchiveClientPayload>) {
  try {
    const { clientId, coachId, email, password, clientGroupId } = payload;
    const variables: IArchiveClientPayload = { clientId, coachId, email };

    if (password) {
      variables.password = password;
    }

    const result: IGraphQLResponse<IArchieveClientResponse> = yield call(
      client.mutate.bind(client),
      {
        mutation: ARCHIVE_CLIENT_MUTATION,
        variables,
      }
    );

    toast.success(i18n.t('clientTable.archiveClient.toastMessage'), toastStyles.success);
    yield put(archiveClientSuccess(result.data.deleteClient));
    yield put(
      fetchClientsRequest({
        coachId,
        take: 10,
        skip: 0,
        isArchived: false,
        nameText: '',
        clientGroupId,
      })
    );
  } catch (error: unknown) {
    if (error instanceof Error) {
      yield put(archiveClientFailure(error.message));
      if (error.message === 'Forbidden resource') {
        toast.error(i18n.t('forbiddenResourceErrorMessage'), toastStyles.error);
      } else {
        toast.error(error.message, toastStyles.error);
      }
    }
  }
}

function* watchGetCompletedActivitiesRequest({
  payload,
}: PayloadAction<IGetCompletedActivityPayload>) {
  const { coachId, from, to, skip, take } = payload;
  try {
    const result: IGraphQLResponse<IGetCompletedActivityResponse> = yield call(
      client.query.bind(client),
      {
        query: GET_ACTIVITIES_COMPLETED_BY_CLIENT,
        variables: {
          coachId,
          from,
          to,
          skip,
          take,
        },
      }
    );
    yield put(
      getCompletedActivitiesSuccess(
        categorizeActivities(result.data.getClientCompletedActivities.completedActivities)
      )
    );
    yield put(setFetchedActivityPaginationData({ to, from }));
  } catch (error: unknown) {
    if (error instanceof Error) yield put(getCompletedActivitiesFailure(error.message));
  }
}

function* watchGetEarlierCompletedActivitiesRequest({
  payload,
}: PayloadAction<IGetCompletedActivityPayload>) {
  const { coachId, from, to, skip, take } = payload;
  try {
    const result: IGraphQLResponse<IGetCompletedActivityResponse> = yield call(
      client.query.bind(client),
      {
        query: GET_ACTIVITIES_COMPLETED_BY_CLIENT,
        variables: {
          coachId,
          from,
          to,
          skip,
          take,
        },
      }
    );
    yield put(
      getEarlierActivitiesSuccess(
        categorizeActivities(result.data.getClientCompletedActivities.completedActivities)
      )
    );
    yield put(setFetchedActivityPaginationData({ to, from }));
  } catch (error: unknown) {
    if (error instanceof Error) yield put(getEarlierActivitiesFailure(error.message));
  }
}

function* watchGetSelectedClientObjectivesDataRequest({
  payload,
}: PayloadAction<IFetchObjectivesDataPayload>) {
  const { fromDate, toDate, clientId } = payload;
  try {
    const result: IGraphQLResponse<IGetObjectiveProgressDataResponse> = yield call(
      client.query.bind(client),
      {
        query: GET_CLIENT_OBJECTIVES_PROGRESS_DATA,
        variables: {
          fromDate,
          toDate,
          clientId,
        },
      }
    );
    yield put(getSelectedClientObjectivesDataSuccess(result.data.getObjectiveProgress));
  } catch (error: unknown) {
    if (error instanceof Error) yield put(getSelectedClientObjectivesDataFailure(error.message));
  }
}

function* watchGetUserActivityProgressRequest({
  payload,
}: PayloadAction<IGetUserActivityProgressPayload>) {
  const { clientId, coachId } = payload;
  try {
    const result: IGraphQLResponse<IGetUserActivityProgressResponse> = yield call(
      client.query.bind(client),
      {
        query: GET_USER_ACTIVITY_PROGRESS,
        variables: {
          clientId,
          coachId,
        },
        fetchPolicy: 'network-only',
      }
    );
    yield put(getUserActivityProgressSuccess(result.data.progressSummary));
  } catch (error: unknown) {
    if (error instanceof Error) yield put(getUserActivityProgressFailure(error.message));
  }
}

function* watchGenerateClientActivityStatusSummeryRequest({
  payload,
}: PayloadAction<{ coachId: string; timeZone: string }>) {
  try {
    const { coachId, timeZone } = payload;
    yield call(client.query.bind(client), {
      query: CLIENT_ACTIVITY_SUMMARY_GENERATION,
      variables: {
        coachId,
        timeZone,
      },
    });
    yield put(generateClientActivityStatusSummerySuccess());
    toast.success(i18n.t('clientTable.toastMessage.activitySummarySent'), toastStyles.success);
  } catch (error: unknown) {
    if (error instanceof Error) {
      yield put(generateClientActivityStatusSummeryFailure());
      if (error.message === 'Forbidden resource') {
        toast.error(i18n.t('forbiddenResourceErrorMessage'), toastStyles.error);
      } else {
        toast.error(error.message, toastStyles.error);
      }
    }
  }
}

function* watchGetClientActivityProgressSummaryRequest({
  payload,
}: PayloadAction<IGetUserActivityProgressPayload>) {
  const { clientId, coachId, skip, take } = payload;
  try {
    const result: IGraphQLResponse<IGetClientActivityProgressSummaryResponse> = yield call(
      client.query.bind(client),
      {
        query: GET_CLIENT_ACTIVITY_PROGRESS_SUMMARY,
        variables: {
          clientId,
          coachId,
          skip,
          take,
        },
        fetchPolicy: 'cache-first',
      }
    );
    yield put(getClientActivityProgressSummarySuccess(result.data.clientActivityProgressSummary));
  } catch (error: unknown) {
    if (error instanceof Error) yield put(getClientActivityProgressSummaryFailure(error.message));
  }
}

function* watchChangeClientStatus({ payload }: PayloadAction<IChangeClientStatusPayload>) {
  const { clientId, status } = payload;
  try {
    const result: IGraphQLResponse<IChangeClientStatusResponse> = yield call(
      client.mutate.bind(client),
      {
        mutation: UPDATE_CLIENT_STATUS_MUTATION,
        variables: {
          clientId,
          status,
        },
      }
    );
    if (result.data.changeClientStatus.isActive) {
      toast.success(i18n.t('clientTable.toastMessage.clientActivated'), toastStyles.success);
    } else {
      toast.success(i18n.t('clientTable.toastMessage.clientDeactivated'), toastStyles.error);
    }
    yield put(changeClientStatusSuccess(result.data.changeClientStatus.isActive));
    yield put(
      fetchClientsRequest({
        coachId: payload.coachId,
        take: 10,
        skip: 0,
        isArchived: false,
        nameText: '',
        clientGroupId: payload.clientGroupId,
      })
    );
  } catch (error: unknown) {
    if (error instanceof Error) {
      yield put(changeClientStatusFailure(error.message));
      yield put(
        fetchClientsRequest({
          coachId: payload.coachId,
          take: 10,
          skip: 0,
          isArchived: false,
          nameText: '',
        })
      );
      if (error.message === 'Forbidden resource') {
        toast.error(i18n.t('forbiddenResourceErrorMessage'), toastStyles.error);
      } else {
        toast.error(error.message, toastStyles.error);
      }
    }
  }
}

function* watchFetchClientPrivacySettings({
  payload,
}: PayloadAction<{ coachId: string; clientId: string }>) {
  const { clientId, coachId } = payload;
  try {
    const result: IGraphQLResponse<IGetPrivacySettingsResponse> = yield call(
      client.mutate.bind(client),
      {
        mutation: GET_CLIENT_CONSENT,
        variables: { clientId, coachId },
      }
    );
    yield put(fetchClientPrivacySettingsSuccess(result.data.getClientConcent));
  } catch (error: unknown) {
    const extractedError = getGraphQLError(error);
    if (extractedError) {
      if (extractedError.code === GraphQLErrorTypes.NOT_FOUND) {
        yield put(setOpenPrivacySettingsConcentModal(true));
      } else if (extractedError.code === GraphQLErrorTypes.FORBIDDEN) {
        yield put(fetchClientPrivacySettingsFailure(extractedError.message));
        toast.error(i18n.t('forbiddenResourceErrorMessage'), toastStyles.error);
      }
    }
  }
}

function* watchCreateClientPrivacySettings({
  payload,
}: PayloadAction<ICreatePrivacySettingsPayload>) {
  try {
    const result: IGraphQLResponse<ICreateClientConcentResponse> = yield call(
      client.mutate.bind(client),
      {
        mutation: CREATE_CLIENT_CONSENT,
        variables: { ...payload },
      }
    );
    yield put(createClientPrivacySettingsSuccess(result.data.createClientConcent));
  } catch (error: unknown) {
    if (error instanceof Error) {
      yield put(fetchClientPrivacySettingsFailure(error.message));
      if (error.message === 'Forbidden resource') {
        toast.error(i18n.t('forbiddenResourceErrorMessage'), toastStyles.error);
      } else {
        toast.error(error.message, toastStyles.error);
      }
    }
  }
}

function* watchUpdateClientPrivacySettings({
  payload,
}: PayloadAction<IUpdatePrivacySettingsPayload>) {
  try {
    const result: IGraphQLResponse<IUpdateClientConcentResponse> = yield call(
      client.mutate.bind(client),
      {
        mutation: UPDATE_CLIENT_CONSENT,
        variables: { ...payload },
      }
    );
    toast.success(t('clientPrivacySettings.updatedToastMessage'), toastStyles.success);
    yield put(updateClientPrivacySettingsSuccess(result.data.updateClientConcent));
  } catch (error: unknown) {
    if (error instanceof Error) {
      yield put(updateClientPrivacySettingsFailure(error.message));
      if (error.message === 'Forbidden resource') {
        toast.error(i18n.t('forbiddenResourceErrorMessage'), toastStyles.error);
      } else {
        toast.error(error.message, toastStyles.error);
      }
    }
  }
}

function* watchResendClientInvitation({ payload }: PayloadAction<{ clientId: string }>) {
  try {
    const { clientId } = payload;
    toast.success(t('clientTable.actions.resendClientInvitationStarted'), toastStyles.success);
    const result = yield call(client.mutate.bind(client), {
      mutation: RESEND_CLIENT_INVITATION,
      variables: { clientId },
    });
    if (result.data.resendClientInvitation.success) {
      toast.success(t(result.data.resendClientInvitation.message), toastStyles.success);
    } else {
      toast.error(t(result.data.resendClientInvitation.message), toastStyles.error);
    }
  } catch (error: unknown) {
    if (error instanceof Error) {
      toast.error(error.message, toastStyles.error);
    }
  }
}

export function* watchClientSagas() {
  yield takeLatest(fetchClientsRequest, watchFetchClients);
  yield takeLatest(fetchArchivedClients, watchFetchClients);
  yield takeLatest(fetchSelectedClientRequest, watchFetchSelectedClient);
  yield takeLatest(addClientRequest, watchAddClientRequest);
  yield takeLatest(addClientGroupRequest, watchAddClientGroupRequest);
  yield takeLatest(archiveClientRequest, watchArchieveClientRequest);
  yield takeLatest(getCompletedActivitiesRequest, watchGetCompletedActivitiesRequest);
  yield takeLatest(getEarlierActivitiesRequest, watchGetEarlierCompletedActivitiesRequest);
  yield takeLatest(
    getSelectedClientObjectivesDataRequest,
    watchGetSelectedClientObjectivesDataRequest
  );
  yield takeLatest(getUserActivityProgressRequest, watchGetUserActivityProgressRequest);
  yield takeLatest(
    generateClientActivityStatusSummeryRequest,
    watchGenerateClientActivityStatusSummeryRequest
  );
  yield takeLatest(
    getClientActivityProgressSummaryRequest,
    watchGetClientActivityProgressSummaryRequest
  );
  yield takeLatest(changeClientStatus, watchChangeClientStatus);
  yield takeLatest(fetchClientPrivacySettings, watchFetchClientPrivacySettings);
  yield takeLatest(createClientPrivacySettings, watchCreateClientPrivacySettings);
  yield takeLatest(updateClientPrivacySettings, watchUpdateClientPrivacySettings);
  yield takeLatest(resendClientInvitation, watchResendClientInvitation);
}

const clientSagas = watchClientSagas;
export default clientSagas;
